import * as React from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { cn } from "../../lib/utils";

export type PageProps = React.HTMLAttributes<HTMLDivElement> & {
  /**
   * `content` is the default reading width. `narrow` suits single-column forms
   * and detail records; `full` lets dense tables use the whole viewport.
   */
  width?: "content" | "narrow" | "full";
};

const widthStyles = {
  content: "max-w-content",
  narrow: "max-w-3xl",
  full: "max-w-none",
};

/**
 * The page container every screen sits in. It owns the gutters and the reading
 * width so screens never invent their own.
 */
export const Page = React.forwardRef<HTMLDivElement, PageProps>(
  ({ className, width = "content", ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "mx-auto flex w-full flex-col gap-5 px-4 py-5 sm:px-6 lg:px-8",
        widthStyles[width],
        className,
      )}
      {...props}
    />
  ),
);
Page.displayName = "Page";

export type Breadcrumb = {
  label: string;
  /** Omit on the current page; the last crumb is rendered as plain text. */
  to?: string;
};

export type PageHeaderProps = {
  title: React.ReactNode;
  description?: React.ReactNode;
  /** Ancestors only. The current page is appended from `title`. */
  breadcrumbs?: Breadcrumb[];
  /** Status badges or counts shown beside the title. */
  meta?: React.ReactNode;
  /** Primary and secondary actions, most important last in the DOM. */
  actions?: React.ReactNode;
  /** Tabs or a sub-navigation strip pinned under the header. */
  children?: React.ReactNode;
  className?: string;
  /** Heading level, for screens that nest a header inside another region. */
  as?: "h1" | "h2";
};

/**
 * The one page header. It provides the single `h1`, the breadcrumb trail back
 * up the resource hierarchy, and the action cluster, so a screen never has to
 * decide where its title goes.
 */
export function PageHeader({
  title,
  description,
  breadcrumbs,
  meta,
  actions,
  children,
  className,
  as: Heading = "h1",
}: PageHeaderProps) {
  return (
    <header className={cn("flex flex-col gap-3", className)}>
      {breadcrumbs && breadcrumbs.length > 0 && (
        <nav aria-label="Breadcrumb">
          <ol className="flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
            {breadcrumbs.map((crumb) => (
              <li key={`${crumb.label}-${crumb.to ?? ""}`} className="flex items-center gap-1">
                {crumb.to ? (
                  <Link
                    to={crumb.to}
                    className="rounded-sm px-1 py-0.5 underline-offset-4 hover:text-foreground hover:underline"
                  >
                    {crumb.label}
                  </Link>
                ) : (
                  <span className="px-1 py-0.5">{crumb.label}</span>
                )}
                <ChevronRight aria-hidden="true" className="h-3 w-3 shrink-0" />
              </li>
            ))}
            <li aria-current="page" className="px-1 py-0.5 text-foreground">
              {typeof title === "string" ? title : "Current page"}
            </li>
          </ol>
        </nav>
      )}

      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <Heading className="min-w-0 truncate text-xl md:text-2xl">
              {title}
            </Heading>
            {meta}
          </div>
          {description && (
            <p className="max-w-2xl text-sm text-muted-foreground">
              {description}
            </p>
          )}
        </div>

        {actions && (
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            {actions}
          </div>
        )}
      </div>

      {children}
    </header>
  );
}

export type PageSectionProps = Omit<React.HTMLAttributes<HTMLElement>, "title"> & {
  title?: React.ReactNode;
  description?: React.ReactNode;
  actions?: React.ReactNode;
};

/**
 * A titled region inside a page. Sections are landmarks, so screen-reader users
 * can jump between them instead of reading a page as one block.
 */
export const PageSection = React.forwardRef<HTMLElement, PageSectionProps>(
  ({ className, title, description, actions, children, ...props }, ref) => {
    const headingId = React.useId();

    return (
      <section
        ref={ref}
        aria-labelledby={title ? headingId : undefined}
        className={cn("flex flex-col gap-3", className)}
        {...props}
      >
        {(title || actions) && (
          <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              {title && (
                <h2 id={headingId} className="text-base">
                  {title}
                </h2>
              )}
              {description && (
                <p className="text-sm text-muted-foreground">{description}</p>
              )}
            </div>
            {actions && (
              <div className="flex shrink-0 items-center gap-2">{actions}</div>
            )}
          </div>
        )}
        {children}
      </section>
    );
  },
);
PageSection.displayName = "PageSection";
